import React, { createContext, useContext, useMemo, useReducer } from "react";

import { Activation } from "@/types";
import { activationReducer } from "./ActivationReducer";
import {
  ActivationAction,
  ActivationState,
  CHANGE_ACTIVATION,
  LOAD_ACTIVATION,
  SHARE_ACTIVATION,
  initialState,
} from "./types";

/** Everything that changes the activation being edited, bound to the reducer */
export const bindActions = (dispatch: React.Dispatch<ActivationAction>) => ({
  /** Starts editing an activation, e.g. a saved one or a new default */
  load: (activation: Activation) => dispatch({ type: LOAD_ACTIVATION, payload: activation }),
  /** Clears the activation so the new activation page starts afresh */
  create: () => dispatch({ type: LOAD_ACTIVATION, payload: null }),
  reset: () => dispatch({ type: LOAD_ACTIVATION, payload: null }),
  /** Merges changed fields into the activation */
  change: (changes: Partial<Activation>) => dispatch({ type: CHANGE_ACTIVATION, payload: changes }),
  shared: (campaignId: string, userIds: number[]) =>
    dispatch({ type: SHARE_ACTIVATION, payload: { campaignId, userIds } }),
});

export type ActivationActions = ReturnType<typeof bindActions>;

const ActivationStateContext = createContext<ActivationState | undefined>(undefined);
const ActivationActionsContext = createContext<ActivationActions | undefined>(undefined);

export const ActivationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(activationReducer, initialState);

  // dispatch never changes, so neither do the actions
  const actions = useMemo(() => bindActions(dispatch), [dispatch]);

  return (
    <ActivationActionsContext.Provider value={actions}>
      <ActivationStateContext.Provider value={state}>
        {children}
      </ActivationStateContext.Provider>
    </ActivationActionsContext.Provider>
  );
};

/** The activation being edited. Re-renders whenever it changes. */
export const useActivationContext = () => {
  const state = useContext(ActivationStateContext);
  if (state === undefined) {
    throw new Error("useActivationContext must be used within an ActivationProvider");
  }
  return state;
};

/** Actions that change the activation being edited. Never changes identity. */
export const useActivationActions = () => {
  const actions = useContext(ActivationActionsContext);
  if (actions === undefined) {
    throw new Error("useActivationActions must be used within an ActivationProvider");
  }
  return actions;
};
